import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Upload, Image as ImageIcon, Crop } from "lucide-react";
import { isImageMedia, mediaRecordUrl, resolveMediaUrl } from "@/lib/editorialMedia.mjs";
import { formatEditorialAspect } from "@/lib/editorialMediaFrame.mjs";

const API_ROOT = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
const authHeaders = () => {
  const token = localStorage.getItem("anokina_token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

async function uploadImageDirect(file) {
  const fd = new FormData();
  fd.append("file", file);
  const res = await axios.post(`${API_ROOT}/api/admin/media/upload-file`, fd, {
    headers: { ...authHeaders(), "Content-Type": "multipart/form-data" },
  });
  return res.data;
}

export function ImageSlot({
  label,
  url,
  onFile,
  onClear,
  onOpenLibrary,
  onOpenCrop,
  focalX = 0.5,
  focalY = 0.5,
  zoom = 1,
  frameAspect = 4 / 5,
  frameLabel = "Кадр публикации",
  testid,
  clearLabel = "Убрать",
}) {
  const [busy, setBusy] = useState(false);
  const [sourceRatio, setSourceRatio] = useState("");
  const safeAspect = Number.isFinite(frameAspect) && frameAspect > 0 ? frameAspect : 4 / 5;

  const handleFile = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const media = await uploadImageDirect(file);
      onFile(media);
      toast.success("Фото загружено");
    } catch (error) {
      toast.error("Не удалось загрузить: " + (error?.response?.data?.detail || error.message));
    } finally {
      setBusy(false);
      event.target.value = "";
    }
  };

  return (
    <div className="space-y-2" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-widest opacity-70">{label}</div>
      <div className="text-[11px] leading-snug text-espresso/60">
        {frameLabel}: {formatEditorialAspect(safeAspect)}. Положение и приближение задаются через «Настроить кадр».
      </div>
      {url ? (
        <>
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
            <div>
              <div className="mb-1 text-[10px] text-espresso/55">Исходное фото</div>
              <div className="relative w-full overflow-hidden rounded-lg bg-neutral-100"
                style={{ aspectRatio: safeAspect }} data-testid={`${testid}-source-preview`}>
                <img src={resolveMediaUrl(url)} alt="Исходное изображение"
                  onLoad={(e) => {
                    const { naturalWidth: w, naturalHeight: h } = e.currentTarget;
                    if (!w || !h) return;
                    const ratio = w / h;
                    setSourceRatio(ratio < 0.7 ? "вертикальное 9:16" : ratio < 0.9 ? "вертикальное" : ratio < 1.1 ? "квадрат 1:1" : "горизонтальное");
                  }}
                  className="h-full w-full object-contain pointer-events-none" />
              </div>
            </div>
            <div>
              <div className="mb-1 text-[10px] text-espresso/55">{frameLabel}</div>
              <div className="relative w-full overflow-hidden rounded-lg bg-neutral-100"
                style={{ aspectRatio: safeAspect }} data-testid={`${testid}-preview`}>
                <img src={resolveMediaUrl(url)} alt="Предварительный результат кадрирования"
                  className="h-full w-full object-cover pointer-events-none"
                  style={{
                    objectPosition: `${focalX * 100}% ${focalY * 100}%`,
                    transform: `scale(${zoom || 1})`,
                    transformOrigin: `${focalX * 100}% ${focalY * 100}%`,
                  }} />
              </div>
            </div>
          </div>
          {sourceRatio && (
            <div className="text-[11px] text-espresso/60">
              Исходное изображение: {sourceRatio}. Текущий кадр: {formatEditorialAspect(safeAspect)}.
            </div>
          )}
          <div className="flex flex-wrap gap-2">
            <label className="cursor-pointer inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl border border-espresso/25 text-[12px]">
              <input type="file" accept="image/*" className="hidden" onChange={handleFile} data-testid={`${testid}-file`} />
              <Upload className="w-3.5 h-3.5" /> {busy ? "Загрузка…" : "Заменить"}
            </label>
            <button type="button" onClick={onOpenLibrary}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl border border-espresso/25 text-[12px]"
              data-testid={`${testid}-library`}>
              <ImageIcon className="w-3.5 h-3.5" /> Из Медиатеки
            </button>
            <button type="button" onClick={onOpenCrop}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl border border-espresso/25 text-[12px]"
              data-testid={`${testid}-crop`}>
              <Crop className="w-3.5 h-3.5" /> Настроить кадр
            </button>
            {onClear && (
              <button type="button" onClick={onClear} className="text-[12px] opacity-60 hover:opacity-100"
                data-testid={`${testid}-clear`}>{clearLabel}</button>
            )}
          </div>
        </>
      ) : (
        <div className="rounded-lg bg-cream/80 border border-dashed border-espresso/25 flex flex-col items-center justify-center gap-2 p-4"
          style={{ aspectRatio: safeAspect }}>
          <ImageIcon className="w-6 h-6 opacity-50" />
          <div className="flex flex-wrap gap-2 justify-center">
            <label className="cursor-pointer inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-espresso text-cream text-[12px]">
              <input type="file" accept="image/*" className="hidden" onChange={handleFile} data-testid={`${testid}-file`} />
              <Upload className="w-3.5 h-3.5" /> {busy ? "Загрузка…" : "Загрузить с устройства"}
            </label>
            <button type="button" onClick={onOpenLibrary}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl border border-espresso/25 text-[12px]"
              data-testid={`${testid}-library`}>
              <ImageIcon className="w-3.5 h-3.5" /> Выбрать из Медиатеки
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export function MediaLibraryPicker({ open, onClose, onPick }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [query, setQuery] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_ROOT}/api/admin/media`, { headers: authHeaders() });
      setItems((res.data || []).filter((m) => mediaRecordUrl(m) && isImageMedia(m)));
    } catch (error) {
      toast.error("Не удалось загрузить Медиатеку");
    } finally {
      setLoading(false);
    }
  };

  if (open && items.length === 0 && !loading) load();
  if (!open) return null;

  const filtered = items.filter((m) => !query.trim() ||
    (m.title || m.filename || "").toLowerCase().includes(query.toLowerCase()));

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center px-4 py-6"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }} data-testid="editorial-media-picker">
      <div className="w-full max-w-4xl max-h-[80vh] overflow-hidden bg-cream rounded-2xl flex flex-col">
        <div className="p-5 border-b border-espresso/10 flex items-center gap-3">
          <div className="font-serif text-xl text-espresso flex-1">Медиатека</div>
          <Input placeholder="Поиск…" value={query} onChange={(e) => setQuery(e.target.value)} className="max-w-[240px]" />
          <button className="text-sm opacity-70 hover:opacity-100" onClick={onClose}>Закрыть</button>
        </div>
        <div className="p-5 overflow-y-auto grid grid-cols-2 md:grid-cols-4 gap-3">
          {loading && <div className="opacity-60 text-sm">Загрузка…</div>}
          {!loading && filtered.length === 0 && <div className="opacity-60 text-sm">Ничего не найдено</div>}
          {filtered.map((m) => {
            const src = mediaRecordUrl(m);
            return (
              <button key={m.id} onClick={() => onPick(m)} className="text-left group"
                data-testid={`editorial-media-item-${m.id}`}>
                <div className="aspect-square rounded-lg overflow-hidden bg-neutral-100">
                  <img src={src} alt="" className="w-full h-full object-cover group-hover:scale-105 transition" />
                </div>
                <div className="text-xs truncate mt-1 opacity-70">{m.title || m.filename}</div>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}
